import secureAxios from "./axios/secureAxios";

/**
 * Fetches discovery data for model/workflow selector
 * Returns trending, recent, and categories with curated items
 * @param {Object} options
 * @param {string} options.type - 'all', 'models', 'workflows'
 * @param {string} options.search - Search query (optional, overrides curated view)
 * @param {boolean} options.zenEnabled - Filter by zen_enabled (default: true)
 * @param {number} options.trendingLimit - Number of trending items (default: 10)
 * @param {number} options.recentLimit - Number of recent items (default: 10)
 * @param {number} options.categoryItemLimit - Items per category (default: 6)
 * @returns {Promise<{
 *   success: boolean,
 *   data: {
 *     trending?: Array,
 *     recent?: Array,
 *     categories?: Array,
 *     results?: Array,
 *     query?: string
 *   }
 * }>}
 */
async function getDiscoveryData({
  type = 'all',
  search = null,
  zenEnabled = true,
  trendingLimit = 10,
  recentLimit = 10,
  categoryItemLimit = 6,
} = {}) {
  const params = new URLSearchParams({
    type,
    zen_enabled: zenEnabled ? '1' : '0',
    trending_limit: String(trendingLimit),
    recent_limit: String(recentLimit),
    category_item_limit: String(categoryItemLimit),
  });
  
  if (search) {
    params.set('search', search);
  }

  const { data } = await secureAxios.get(`/discovery?${params}`);
  return data;
}

/**
 * Search models and workflows
 * @param {string} query - Search query
 * @param {string} type - 'all', 'models', 'workflows'
 * @param {boolean} zenEnabled - Filter by zen_enabled
 * @returns {Promise<{success: boolean, data: {results: Array, query: string}}>}
 */
async function searchDiscovery(query, type = 'all', zenEnabled = true) {
  return getDiscoveryData({ type, search: query, zenEnabled });
}

/**
 * Get paginated items from a specific section
 * @param {string} section - 'trending', 'recent', or category slug
 * @param {Object} options
 * @param {string} options.type - 'all', 'models', 'workflows'
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 20)
 * @param {boolean} options.zenEnabled - Filter by zen_enabled
 * @returns {Promise<{
 *   success: boolean,
 *   data: {
 *     items: Array,
 *     pagination: { page, limit, total, totalPages, hasMore }
 *   }
 * }>}
 */
async function getSectionItems(section, {
  type = 'all',
  page = 1,
  limit = 20,
  zenEnabled = true,
} = {}) {
  const params = new URLSearchParams({
    type,
    page: String(page),
    limit: String(limit),
    zen_enabled: zenEnabled ? '1' : '0',
  });

  const { data } = await secureAxios.get(`/discovery/section/${section}?${params}`);
  return data;
}

/**
 * Get zen-enabled categories with preview images for first-time user onboarding
 * Returns categories that have zen-enabled models/workflows with preview thumbnails
 * @returns {Promise<{
 *   success: boolean,
 *   data: Array<{
 *     category_id: number,
 *     name: string,
 *     slug: string,
 *     description: string,
 *     icon: string,
 *     icon_url: string,
 *     accent_color: string,
 *     cover_image_url: string,
 *     preview_images: string[],
 *     model_count: number,
 *     workflow_count: number,
 *     total_count: number,
 *     items: Array<{id: string, name: string, type: 'model'|'workflow', image: string}>
 *   }>
 * }>}
 */
async function getZenCategories() {
  const { data } = await secureAxios.get('/categories/zen');
  return data;
}

export const discoveryActions = {
  getDiscoveryData,
  searchDiscovery,
  getSectionItems,
  getZenCategories,
};

export default discoveryActions;
